import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react'
import { Snackbar, Alert, AlertColor } from '@mui/material'
import { setToastImplementations, ApiError } from '../utils/errorHandler'

interface ToastContextType {
  showToast: (message: string, severity?: AlertColor) => void
  showErrorToast: (message: string) => void
  showSuccessToast: (message: string) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

interface ToastProviderProps {
  children: ReactNode
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toast, setToast] = useState<{
    open: boolean
    message: string
    severity: AlertColor
  }>({
    open: false,
    message: '',
    severity: 'info'
  })

  const showToast = (message: string, severity: AlertColor = 'info') => {
    setToast({
      open: true,
      message,
      severity
    })
  }

  const showErrorToast = (message: string) => {
    showToast(message, 'error')
  }

  const showSuccessToast = (message: string) => {
    showToast(message, 'success')
  }

  // Set the toast implementations for the error handler
  useEffect(() => {
    setToastImplementations(
      (error: ApiError) => showErrorToast(error.message),
      (message: string) => showSuccessToast(message)
    )
  }, [])

  const handleClose = () => {
    setToast(prev => ({ ...prev, open: false }))
  }

  return (
    <ToastContext.Provider value={{ showToast, showErrorToast, showSuccessToast }}>
      {children}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleClose} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  )
}
